import { FC, useState, useReducer, useEffect } from 'react'
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import { SelectChangeEvent } from '@mui/material/Select'

import {
  Challan,
  fetchChallanDetail,
  ItemEntryDetail,
  UpdateEntryDateRequest,
} from '../../../services'

// Components
import { AppDialog, ChallanSection } from '../../../components'

// Redux
import { useAppDispatch, useAppSelector } from '../../../hooks'
import {
  refreshFetchSlipsAction,
  updateEntryDateAction,
  resetEntryDateAction,
} from '../../../store/slips'
import { showAlertAction } from '../../../store/alerts'
import { getFormattedDate, replace12AmWithCurrentTime } from '../../../utils'
import CustomNumberField from '../Users/profile/CustomNumberField'
import SelectStateItem, { getState, StateOption } from '../Customers/SelectStateItem'

export interface EditEntryDateProps {
  onClose?: () => void
  onSubmitSuccess?: () => void
  item: ItemEntryDetail
  accountId: number
}

export const EditEntryDate: FC<EditEntryDateProps> = ({
  onClose,
  onSubmitSuccess,
  item,
  accountId
}) => {
  const dispatchAction = useAppDispatch()
  const {
    success: updateEntryDateSuccess,
    error: updateEntryDateError,
    message,
  } = useAppSelector((state) => state.updateEntryDate)

  const [fetchedItem, setFetchedItem] = useState<ItemEntryDetail | null>(null)
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false)
  const [date, setDate] = useState<Date | null>(new Date())
  const [isClose] = useState<boolean>(false)
  const [request, setRequest] = useState<UpdateEntryDateRequest | null>(null)
  const [vehicleNo, setVehicleNo] = useState<string | null>(null)
  const [ewayBillNo, setEwayBillNo] = useState<number | null>(null)
  const [shipmentNo, setShipmentNo] = useState<string>('')
  const [selectedChallanNo, setSelectedChallanNo] = useState<string | null>(null)
  const { data: userData } = useAppSelector((state) => state.userData)
  const [prefix, setPrefix] = useState<string | null>(null)
  // New fields for DL, driverName, driverMobile
  const [dl, setDL] = useState<string | null>('')
  const [driverName, setDriverName] = useState<string | null>('')
  const [driverMobile, setDriverMobile] = useState<string | null>('')
  const [remark, setRemark] = useState<string>('')
  const itemToEdit = fetchedItem ?? item
  const [dispatchLabel, setDispatchLabel] = useState<string>('Dispatch From')
  const [dispatchType, setDispatchType] = useState<'GODOWN' | 'CUSTOM'>('GODOWN')
  const [addressId, setAddressId] = useState<number | null>(null)
  const [address1, setAddress1] = useState<string>('')
  const [address2, setAddress2] = useState<string>('')
  const [place, setPlace] = useState<string>('')
  const [pincode, setPincode] = useState<string>('')
  const [stateOption, setStateOption] = useState<StateOption | null>(null)
  const [address1Error, setAddress1Error] = useState<boolean>(false)

  useEffect(() => {
    let isMounted = true

    if (!item?.challanId) {
      setFetchedItem(item)
      return
    }

    setLoadingDetails(true)
    fetchChallanDetail(item.challanId)
      .then((response) => {
        if (!isMounted) return
        if (response?.data?.success && response?.data?.data) {
          setFetchedItem(response.data.data)
        } else {
          setFetchedItem(item)
          if (response?.data?.message) {
            dispatchAction(
              showAlertAction({
                message: response.data.message,
                severity: 'error',
              })
            )
          }
        }
      })
      .catch(() => {
        if (!isMounted) return
        setFetchedItem(item)
        dispatchAction(
          showAlertAction({
            message: 'Failed to load latest challan details',
            severity: 'error',
          })
        )
      })
      .finally(() => {
        if (isMounted) {
          setLoadingDetails(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [dispatchAction, item])

  useEffect(() => {
    setDate(
      new Date(itemToEdit?.titleDate != null ? itemToEdit?.titleDate : Date())
    )
    setVehicleNo(itemToEdit?.vehicleNo ?? '')
    setEwayBillNo(itemToEdit?.ewayBillNo ?? null)
    setShipmentNo(itemToEdit?.shipmentNo ?? '')
    setSelectedChallanNo(itemToEdit?.chaalanNumber ?? itemToEdit?.displayCHL ?? '')
    setDL(itemToEdit?.DL ?? '')
    setDriverName(itemToEdit?.driverName ?? '')
    setDriverMobile(itemToEdit?.driverMobile ?? '')
    setRemark(itemToEdit?.challanRemark ?? '')
    setDispatchLabel(itemToEdit?.dispatchLabel ?? 'Dispatch From')

    if (itemToEdit?.address?.add1) {
      setDispatchType('CUSTOM')
      setAddressId(itemToEdit.address.id ?? null)
      setAddress1(itemToEdit.address.add1 ?? '')
      setAddress2(itemToEdit.address.add2 ?? '')
      setPlace(itemToEdit.address.place ?? '')
      setPincode(
        itemToEdit.address.pincode != null ? String(itemToEdit.address.pincode) : ''
      )
      setStateOption(getState(itemToEdit.address.stateCode ?? ''))
    } else {
      setDispatchType('GODOWN')
      setAddressId(itemToEdit?.address?.id ?? null)
      setAddress1('')
      setAddress2('')
      setPlace('')
      setPincode('')
      setStateOption(null)
    }
    setAddress1Error(false)
  }, [itemToEdit])

  useEffect(() => {
    if(userData.config) {
     if(itemToEdit?.challanType === 'OUT') {
       setPrefix(userData.config.outwardChallanPrefix)
     } else if(itemToEdit?.challanType === 'IN') {
       setPrefix(userData.config.inwardChallanPrefix)
     }
    }
   }, [itemToEdit?.challanType, userData])

  useEffect(() => {
    if (updateEntryDateSuccess) {
      dispatchAction(
        showAlertAction({
          message: 'Updated Successfully',
          severity: 'success',
        })
      )
      onSubmitSuccess() // To close the popup
      dispatchAction(refreshFetchSlipsAction())
      dispatchAction(resetEntryDateAction())
    } else if (updateEntryDateError && message) {
      dispatchAction(showAlertAction({ message: message, severity: 'error' }))
      onSubmitSuccess() // To close the popup
      dispatchAction(resetEntryDateAction())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateEntryDateSuccess, message, updateEntryDateError])

  const dateChangeHandler = (value) => {
    setDate(replace12AmWithCurrentTime(value))
  }

  useEffect(() => {
    if (!itemToEdit?.challanId) return

    const itemEntries = itemToEdit?.itemEntries ?? []
    const entries = itemEntries.map((entry) => {
      return { id: entry.id }
    })

    const entryDateSource =
      date ?? new Date(itemToEdit?.titleDate != null ? itemToEdit.titleDate : Date.now())

    const customAddress =
      dispatchType === 'CUSTOM'
        ? {
            ...(addressId != null && { id: addressId }),
            add1: address1,
            add2: address2,
            place: place,
            pincode: pincode ? Number(pincode) : undefined,
            state: stateOption?.label,
            stateCode: stateOption?.code,
          }
        : {}

    setRequest({
      entryDate: getFormattedDate(entryDateSource),
      entries: entries,
      vehicleNo: vehicleNo,
      ewayBillNo: ewayBillNo,
      shipmentNo: shipmentNo ?? '',
      challanId: itemToEdit?.challanId,
      challanNo: selectedChallanNo,
      DL: dl,
      driverName: driverName,
      driverMobile: driverMobile,
      challanRemark: remark ?? '',
      address: customAddress,
    })
  }, [
    itemToEdit,
    date,
    vehicleNo,
    ewayBillNo,
    shipmentNo,
    dl,
    driverName,
    driverMobile,
    remark,
    selectedChallanNo,
    dispatchType,
    addressId,
    address1,
    address2,
    place,
    pincode,
    stateOption,
  ])

  const submitHandle = () => {
    if (dispatchType === 'CUSTOM' && !address1.trim()) {
      setAddress1Error(true)
      return
    }

    const checkRequest = request.entryDate
    if (checkRequest) {
      const requestToSend = {
        ...request,
        ewayBillNo: request.ewayBillNo == null || request.ewayBillNo === '' ? 0 : request.ewayBillNo,
        address:
          dispatchType === 'GODOWN'
            ? {}
            : {
                ...(addressId != null && { id: addressId }),
                add1: address1.trim(),
                add2: address2.trim(),
                place: place.trim(),
                pincode: pincode ? Number(pincode) : undefined,
                state: stateOption?.label,
                stateCode: stateOption?.code,
              },
      }
      dispatchAction(updateEntryDateAction(requestToSend))
    }
  }

  const onChangeHandler = (e) => {
    const { value } = e.target
    setVehicleNo(value)
    setRequest({
      ...request,
      vehicleNo: value,
    })
  }

  const onEwayChange = (e) => {
    const { value } = e.target
    setEwayBillNo(value)
    setRequest({
      ...request,
      ewayBillNo: value,
    })
  }

  const onChallanChange = (e) => {
    const { value } = e.target
    setSelectedChallanNo(value)
    setRequest({
      ...request,
      challanNo: value,
    })
  }

  const onShipmentNoChange = (e) => {
    const { value } = e.target
    setShipmentNo(value)
    setRequest({
      ...request,
      shipmentNo: value ?? '',
    })
  }

  const onDLChange = (e) => {
    const { value } = e.target
    setDL(value)
    setRequest({
      ...request,
      DL: value,
    })
  }

  const onDriverNameChange = (e) => {
    const { value } = e.target
    setDriverName(value)
    setRequest({
      ...request,
      driverName: value,
    })
  }

  const onDriverMobileChange = (e) => {
    const { value } = e.target
    setDriverMobile(value)
    setRequest({
      ...request,
      driverMobile: value,
    })
  }

  const onRemarkChange = (e) => {
    const { value } = e.target
    setRemark(value)
    setRequest({
      ...request,
      challanRemark: value ?? '',
    })
  }

  const onDispatchTypeChange = (event: SelectChangeEvent) => {
    const value = event.target.value as 'GODOWN' | 'CUSTOM'
    setDispatchType(value)
    if (value === 'GODOWN') {
      setAddress1Error(false)
    }
  }

  return (
    <AppDialog
      id="edit-entry-date"
      title={'Edit Challan'}
      submitButtonText="Submit"
      onClose={onClose}
      onSubmit={submitHandle}
      close={isClose}
      maxWidth="sm"
      
      fixHeight={true}
    >
      {loadingDetails ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid
          container
          direction="row"
          justifyContent="center"
          spacing={3}
        >
          <Grid item xs={12}>
            <Box
              component="form"
              sx={{
                width: '96%',
                mx: 'auto',
              }}
              noValidate
              autoComplete="off"
            >
            <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomNumberField
                maxLength={14}
                name="challanNo"
                value={selectedChallanNo}
                label="Challan No"
                fullWidth
                onChange={onChallanChange}
              />
              
            </Grid>

            <Grid item xs={12}>
              <DateTimePicker
                label="Select Date"
                value={date}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
                ampmInClock={false}
                onChange={dateChangeHandler}
                format="dd/MM/yyyy hh:mm a"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small"
                name="vehicleNo"
                fullWidth
                label="Vehicle No"
                value={vehicleNo}
                onChange={onChangeHandler}
                inputProps={{ style: { textTransform: 'uppercase' } }}  // CSS to display text in uppercase

              />
            </Grid>

            <Grid item xs={12}>
              <CustomNumberField
                maxLength={14}
                name="ewayBillNo"
                value={ewayBillNo}
                label="e-Way Bill No"
                fullWidth
                onChange={onEwayChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small"
                name="shipmentNo"
                fullWidth
                label="Shipment No"
                value={shipmentNo}
                inputProps={{ maxLength: 100 }}
                onChange={onShipmentNoChange}
              />
            </Grid>

            {/* New Fields for DL, driverName, driverMobile */}
            <Grid item xs={12}>
              <TextField
                size="small"
                name="DL"
                fullWidth
                label="Transporter ID"
                value={dl}
                inputProps={{
                  style: { textTransform: 'uppercase' },
                  maxLength: 100,
                }} // CSS to display text in uppercase
                onChange={onDLChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small"
                name="driverName"
                fullWidth
                label="Transporter Name"
                value={driverName}
                inputProps={{ maxLength: 100 }}
                onChange={onDriverNameChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small"
                name="driverMobile"
                fullWidth
                label="Transporter Mobile"
                value={driverMobile}
                onChange={onDriverMobileChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small"
                name="challanRemark"
                fullWidth
                label="Remark"
                value={remark}
                onChange={onRemarkChange}
                inputProps={{ maxLength: 200 }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="dispatch-type-label">{dispatchLabel}</InputLabel>
                <Select
                  labelId="dispatch-type-label"
                  value={dispatchType}
                  label={dispatchLabel}
                  onChange={onDispatchTypeChange}
                >
                  <MenuItem value="GODOWN">Godown Address</MenuItem>
                  <MenuItem value="CUSTOM">Custom Address</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {dispatchType === 'CUSTOM' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    name="address1"
                    fullWidth
                    label="Address 1"
                    required
                    value={address1}
                    error={address1Error}
                    helperText={address1Error ? 'Address 1 is required' : ''}
                    onChange={(e) => {
                      setAddress1(e.target.value)
                      if (e.target.value.trim()) {
                        setAddress1Error(false)
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    name="address2"
                    fullWidth
                    label="Address 2"
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    name="place"
                    fullWidth
                    label="Place"
                    value={place}
                    onChange={(e) => setPlace(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomNumberField
                    maxLength={6}
                    name="pincode"
                    value={pincode}
                    label="Pincode"
                    fullWidth
                    onChange={(e) => setPincode(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <SelectStateItem
                    selectedStateOption={stateOption}
                    label={'State'}
                    onChange={(option) => setStateOption(option)}
                  />
                </Grid>
              </>
            )}
            </Grid>
            </Box>
          </Grid>
        </Grid>
      )}
    </AppDialog>
  )
}